import { Avatar, CardContent, Grid, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { MprcCard, MprcCardActions, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcJoinComponents, MprcLink, MprcRequestErrorDialog, MprcSpinnerIndicator } from "components";
import { MprcFilterCount } from "components/filter";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { ManagedEnrollmentTrainingListResponse, ManagedEnrollmentTrainingListResponsePaginatedList, TrainingDueDateOption } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import TzUtils from "lib/TzUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LmsTrainingDefaultThumbnailUrl } from "views/lms/components/LmsTypeImage";
import { EditEnrollmentStepProps } from "views/lms/enrollment/Home";
import { useEnrollmentManagementContext } from "views/lms/enrollment/Home/EnrollmentManagementWrapper";
import { Filter } from "views/lms/enrollment/Home/Training/ListTrainingFilter";
import { TrainingRegistrationDialog } from "views/lms/student/Registration/TrainingRegistrationDialog";

////////////////////////////////////////////////////////////////////////////

export const ListTrainings: React.FC<EditEnrollmentStepProps> = (props) => {
    const ctx = useEnrollmentManagementContext();
    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const [viewData, setViewData] = useState<ManagedEnrollmentTrainingListResponsePaginatedList | null>(null);
    const [viewError, setViewError] = useState<any>();
    const [searchText, setSearchText] = useState<string | undefined>(ctx.filter.searchText);
    const [loading, setLoading] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();

    ////////////////////////////////////////////////////////////////////////////

    const filtered = ctx.filter.searchText.length > 0;

    ///
    const resetFilters = () => {
        ctx.resetFilter();
    };

    type MenuOptions = "TrainingInfo";

    //
    async function loadTrainings() {
        setLoading(true);

        try {
            if (ctx.unitTypes == undefined) {
                let result = await api.v1.unitType.getUnitTypes();
                ctx.setUnitTypes(result.data);
            }

            if (ctx.userSettings == undefined) {
                let result = await api.v1.foundationSettings.getUserSettings();
                ctx.setUserSettings(result.data);
            }

            const result = await api.v1.enrollmentOperation.getEnrollmentTrainings({
                searchText: ctx.filter.searchText || null,
                trainingId: null,
                classroomId: null,
                filter: ctx.filter.advFilters,
                orderBy: ctx.filter.order,
                pageNumber: ctx.pagination.page,
                addCompletedClassroom: ctx.filter.addCompleted,
                pageSize: APP_PAGE_SIZE_DEFAULT
            });

            setViewData(result.data);
        }
        catch (e) {
            setViewData(null);
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    const getMenuItems = useCallback(
        (item: ManagedEnrollmentTrainingListResponse): MprcDropdownItems<MenuOptions> => {

            const groups: MprcDropdownItems<MenuOptions>[] = [];

            const group0: MprcDropdownItems<MenuOptions> = [
                { text: t("Enrollment.StepTraining.ListTraining.TrainingInfoAction"), value: "TrainingInfo" },
            ];
            groups.push(group0);

            return MprcDropdownBuildGroupedItems<MenuOptions>(groups);
        },
        [viewData]
    );

    ///
    const handleSelectOption = (option: MenuOptions, item: ManagedEnrollmentTrainingListResponse) => {
        switch (option) {
            case "TrainingInfo":
                setDialog(<TrainingRegistrationDialog
                    trainingId={item.trainingId}
                    readonly
                    onClose={() => setDialog(null)}
                />);
                break;
        }
    };

    ///
    const handleSelect = (item: ManagedEnrollmentTrainingListResponse) => {
        ctx.setTraining({ ...item, totalSelected: 0 });
        if (!item.multipleClasses && item.classCount == 1) {
            ctx.setClassroom({
                classroomId: item.classroomId,
                name: item.classroomName,
                timezoneId: item.timezoneId,
                enrollSuspended: item.enrollSuspended,
                availableEnrollmentsCount: item.availableEnrollmentsCount,
                executionStartDate: item.executionStartDate,
                executionDueDateOption: item.executionDueDateOption,
                executionDueDate: item.executionDueDate,
                executionDueDays: item.executionDueDays
            });
        } else {
            ctx.setClassroom(undefined);
        }

        props.goNextStep();
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        loadTrainings();
        ctx.clearSelection();
    }, []);

    useEffect(() => {
        loadTrainings();
        if (searchText != ctx.filter.searchText) setSearchText(ctx.filter.searchText);
    }, [ctx.filter, ctx.pagination]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const buildSecondLine = (item: ManagedEnrollmentTrainingListResponse) => {
        let c: React.ReactNode[] = [];

        c.push(<Typography variant="caption" color="muted.main">{item.contentTypeStr}</Typography>);

        if (item.classCount == 1) {

            if (item.enrollSuspended) {
                c.push(<Typography variant="bodyNotice">{t("Enrollment.StepTraining.ListTraining.EnrollmentSuspendedText")}</Typography>);

            } else if (item.executionStartDate != null && item.executionDueDate != null && item.executionStartDate == item.executionDueDate) {
                c.push(
                    <Typography variant="caption" color="muted.main">
                        {
                            t("Enrollment.StepTraining.ListTraining.DateText", {
                                name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionStartDate, item.timezoneId)!, false)
                            })
                        }
                    </Typography>
                );
            } else if (item.executionStartDate == null) {
                c.push(<Typography variant="caption" color="muted.main">{t("Enrollment.StepTraining.ListTraining.AnyTimeText")}</Typography>);
            } else if (item.executionStartDate != null) {
                c.push(
                    <Typography variant="caption" color="muted.main">
                        {
                            t("Enrollment.StepTraining.ListTraining.StartDateText", {
                                name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionStartDate, item.timezoneId)!, false)
                            })
                        }
                    </Typography>
                );
            }

            if (!item.enrollSuspended) {
                if (item.executionDueDateOption == TrainingDueDateOption.UntilDaysAfterStartTraining && item.executionDueDays) {
                    c.push(
                        <Typography variant="caption" color="muted.main">
                            {t("Enrollment.StepTraining.ListTraining.UntilDaysAfterStartText", { count: item.executionDueDays })}
                        </Typography>
                    );
                } else if (item.executionDueDateOption == TrainingDueDateOption.UntilSpecifedDate && item.executionDueDate && item.executionStartDate != item.executionDueDate) {
                    c.push(
                        <Typography variant="caption" color="muted.main">
                            {
                                t("Enrollment.StepTraining.ListTraining.DueDateText", {
                                    name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionDueDate, item.timezoneId)!, false)
                                })}
                        </Typography>
                    );
                }

                if (item.availableEnrollmentsCount > 0) {
                    c.push(
                        <Typography variant="caption" color="muted.main">
                            {t("Enrollment.StepTraining.ListTraining.AvailableEnrollmentText", { count: item.availableEnrollmentsCount })}
                        </Typography>
                    );
                }
            }
        } else if (item.classCount > 1) {
            c.push(<Typography variant="caption" color="muted.main">{t("Enrollment.StepTraining.ListTraining.OpenClassesText", { count: item.classCount })}</Typography>);
        }

        return <Typography variant="caption"><MprcJoinComponents components={c} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
    };

    ///
    const renderContent = () => {
        return (
            <>
                <CardContent>
                    {loading && <MprcSpinnerIndicator />}

                    {
                        !loading && viewData && (
                            <>
                                <MprcContainer>
                                    <MprcFilterCount
                                        loading={loading}
                                        isEmpty={viewData!.totalRows == 0}
                                        countText={t("Enrollment.StepTraining.ListTraining.RecordsFoundText", { count: viewData!.totalRows })}
                                        filter={{
                                            active: filtered,
                                            text: t("Enrollment.StepTraining.ListTraining.RecordsFilteredText", { count: viewData!.totalRows }),
                                            onResetFilter: resetFilters
                                        }}
                                    />
                                </MprcContainer>

                                <Grid container justifyContent="center" alignItems="center" spacing={2}>
                                    {viewData!.items.map((item, index) => {
                                        return (
                                            <Grid key={index} alignItems="center" justifyContent="center" size={{ xs: 12 }}>
                                                <Grid container justifyContent="flex-start" alignItems="flex-start" spacing={1} wrap="nowrap">
                                                    <Grid>
                                                        <MprcLink onClick={() => handleSelect(item)}>
                                                            <Avatar
                                                                variant="rounded"
                                                                sx={{ width: 70, height: 48 }}
                                                                src={LmsTrainingDefaultThumbnailUrl(item.contentType, item.thumbnailUrl)}
                                                            />
                                                        </MprcLink>
                                                    </Grid>
                                                    <Grid size="grow">
                                                        <Grid container direction="row">
                                                            <Grid size={{ xs: 12 }}>
                                                                <Grid container alignItems="center" wrap="nowrap">
                                                                    <Grid >
                                                                        <MprcListItemTitle onClick={() => handleSelect(item)}>
                                                                            {item.name}
                                                                        </MprcListItemTitle>
                                                                    </Grid>
                                                                    <Grid>
                                                                        <MprcIconButtonMoreOptionsDropdown
                                                                            color="muted"
                                                                            onChange={(selection) => handleSelectOption(selection, item)}
                                                                            items={getMenuItems(item)}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid size={{ xs: 12 }} sx={(theme) => ({ mt: theme.spacing(-.5) })}>
                                                                {buildSecondLine(item)}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        );
                                    })
                                    }
                                </Grid>
                            </>
                        )}
                </CardContent>
                {!loading && viewData && viewData.totalPages > 1 && (
                    <MprcCardActions>
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, pageNumber) => {
                                if (pageNumber != ctx.pagination.page) {
                                    ctx.setPagination({ page: pageNumber });
                                }
                            }}
                        />
                    </MprcCardActions>
                )}
            </>
        );
    };

    ///
    return (
        <>
            {dialog}

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            <MprcContainer>
                <MprcCard>
                    {
                        <>
                            <Filter />
                            {renderContent()}
                        </>
                    }
                </MprcCard>
            </MprcContainer>
        </>
    );
};
